import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SubdirectoryArrowLeftIcon from "@mui/icons-material/SubdirectoryArrowLeft";
import { useRemoteDirectoryBrowser } from "../../../hooks/useRemoteDirectoryBrowser";
import {
  ACCENT,
  ACCENT_TINT,
  FONT,
  HAIRLINE,
  HOVER,
  ICON,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SURFACE,
  SURFACE_MUTED,
  WEIGHT,
} from "../../../theme/tokens";

const CODE_EXT = new Set([
  "py",
  "js",
  "jsx",
  "ts",
  "tsx",
  "sh",
  "bash",
  "go",
  "c",
  "cc",
  "cpp",
  "h",
  "hpp",
  "java",
  "rs",
  "rb",
  "r",
]);
const DATA_EXT = new Set(["json", "yaml", "yml", "toml", "ini", "cfg", "env"]);
const DOC_EXT = new Set(["md", "markdown", "txt", "rst", "log"]);
const IMAGE_EXT = new Set(["png", "jpg", "jpeg", "gif", "svg", "webp", "ico"]);

function getFileGlyph(name) {
  const lower = (name || "").toLowerCase();
  const ext = lower.includes(".") ? lower.split(".").pop() : "";
  if (lower.startsWith("dockerfile")) return CodeOutlinedIcon;
  if (ext === "lock" || lower.endsWith(".lock")) return LockOutlinedIcon;
  if (CODE_EXT.has(ext)) return CodeOutlinedIcon;
  if (DATA_EXT.has(ext)) return DataObjectOutlinedIcon;
  if (DOC_EXT.has(ext)) return ArticleOutlinedIcon;
  if (IMAGE_EXT.has(ext)) return ImageOutlinedIcon;
  return InsertDriveFileOutlinedIcon;
}

function formatSize(bytes) {
  if (bytes === 0) return "0 B";
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} K`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / 1024 / 1024).toFixed(1)} M`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} G`;
}

function formatDate(ts) {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
}

function formatOwner(id, userid, username) {
  if (id === 0) return "root";
  if (id === userid) return username || String(id);
  return String(id);
}

function buildBreadcrumbs(rootLabel, currentPath) {
  const parts = currentPath
    .split("/")
    .map((part) => part.trim())
    .filter((part) => part && part !== ".");

  if (parts.length === 0) {
    return [{ label: rootLabel, path: "." }];
  }

  return [
    { label: rootLabel, path: "." },
    ...parts.map((part, index) => ({
      label: part,
      path: parts.slice(0, index + 1).join("/"),
    })),
  ];
}

const GRID_COLS = "minmax(0, 1fr) 96px 88px 88px 70px 86px";
const COL_HEAD_SX = {
  fontSize: FONT.xs,
  color: INK_SECONDARY,
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const COL_META_SX = {
  fontSize: FONT.xs,
  color: INK_MUTED,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "right",
};
const COL_MONO_SX = { ...COL_META_SX, fontFamily: "monospace" };

export default function RemoteDirectoryBrowser({
  height = 300,
  onFileOpen,
  onSelect,
  rowDensity = "compact",
  selectable = false,
  selectLabel = "Select",
  source = "home",
  initialPath = ".",
  rootLabel = "~",
}) {
  const {
    currentPath,
    error,
    fetchDir,
    loading,
    openRow,
    recoverFromError,
    resolveRowPath,
    rows,
    truncated,
    userid,
    username,
  } = useRemoteDirectoryBrowser({ initialPath, source });

  // No row is highlighted until the user clicks or keyboard-navigates.
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(rootLabel, currentPath),
    [currentPath, rootLabel],
  );
  const breadcrumbCopyText = useMemo(() => {
    return currentPath === "." || currentPath === ""
      ? rootLabel
      : `${rootLabel}/${currentPath}`;
  }, [currentPath, rootLabel]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter(
      (row) => row._isParent || row.name.toLowerCase().includes(q),
    );
  }, [rows, searchQuery]);

  const entryCount = rows.filter((r) => !r._isParent).length;

  const rowRefs = useRef([]);
  const isComfortable = rowDensity === "comfortable";

  useEffect(() => {
    setFocusedIdx(-1);
    setSearchQuery("");
  }, [currentPath]);

  useEffect(() => {
    rowRefs.current[focusedIdx]?.scrollIntoView({ block: "nearest" });
  }, [focusedIdx]);

  const activateRow = (row) => {
    if (row.type === "dir") {
      openRow(row);
      return;
    }

    if (onFileOpen) {
      onFileOpen({
        ...row,
        relativePath: resolveRowPath(row),
        currentPath,
        source,
      });
    }
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedIdx((idx) => Math.min(idx + 1, filteredRows.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setFocusedIdx((idx) => Math.max(idx - 1, 0));
        break;
      case "Enter":
        if (filteredRows[focusedIdx]) {
          activateRow(filteredRows[focusedIdx]);
        }
        break;
      default:
        break;
    }
  };

  const handleNavigateToCrumb = (path) => {
    fetchDir(path);
  };

  const handleCopyPath = () => {
    navigator.clipboard?.writeText(breadcrumbCopyText);
  };

  return (
    <Box
      sx={{
        height,
        border: `1px solid ${HAIRLINE}`,
        borderRadius: `${RADIUS.md}px`,
        overflow: "hidden",
        backgroundColor: SURFACE,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Breadcrumb toolbar */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${HAIRLINE}`,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <HomeRoundedIcon sx={{ color: ICON, fontSize: 16, flexShrink: 0 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            rowGap: 0.25,
            minWidth: 0,
            flex: 1,
          }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            const isDisabledRootCrumb = source === "datasets" && index === 0;

            return (
              <Box
                key={crumb.path || "__root__"}
                sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
              >
                {index > 0 && (
                  <ChevronRightIcon
                    sx={{ fontSize: 16, color: INK_MUTED, mx: 0.25 }}
                  />
                )}
                {isLast || isDisabledRootCrumb ? (
                  <Typography
                    sx={{
                      fontSize: FONT.sm,
                      color: isDisabledRootCrumb ? INK_MUTED : INK,
                      fontWeight: 600,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {crumb.label}
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => handleNavigateToCrumb(crumb.path)}
                    sx={{
                      fontSize: FONT.sm,
                      color: INK_SECONDARY,
                      fontWeight: 500,
                      "&:hover": { color: ACCENT },
                    }}
                  >
                    {crumb.label}
                  </Link>
                )}
              </Box>
            );
          })}
        </Box>
        {!loading && !error && (
          <Typography
            sx={{
              fontSize: FONT.xs,
              color: INK_MUTED,
              whiteSpace: "nowrap",
              flexShrink: 0,
              display: { xs: "none", sm: "block" },
            }}
          >
            {entryCount} item{entryCount !== 1 ? "s" : ""}
          </Typography>
        )}
        <Tooltip title="Copy directory path">
          <IconButton
            size="small"
            onClick={handleCopyPath}
            sx={{ color: ICON, flexShrink: 0 }}
          >
            <ContentCopyIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        {selectable && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onSelect?.(source, currentPath)}
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontSize: FONT.xs,
              fontWeight: 600,
              color: ACCENT,
              borderColor: HAIRLINE,
              "&:hover": { borderColor: ACCENT, backgroundColor: ACCENT_TINT },
            }}
          >
            {selectLabel}
          </Button>
        )}
      </Box>

      {/* Search */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${HAIRLINE}`,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <SearchOutlinedIcon sx={{ fontSize: 16, color: ICON, flexShrink: 0 }} />
        <InputBase
          placeholder="Search this directory…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            fontSize: FONT.sm,
            color: INK,
            flex: 1,
            "& input": { p: 0 },
            "& input::placeholder": { color: INK_MUTED, opacity: 1 },
          }}
          inputProps={{ "aria-label": "search files" }}
        />
        {searchQuery && (
          <IconButton
            size="small"
            onClick={() => setSearchQuery("")}
            sx={{ p: 0.25, color: ICON }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
      </Box>

      {/* Column header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: GRID_COLS,
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${HAIRLINE}`,
          backgroundColor: SURFACE_MUTED,
        }}
      >
        <Typography sx={COL_HEAD_SX}>Name</Typography>
        <Typography sx={{ ...COL_HEAD_SX, textAlign: "right" }}>
          Permissions
        </Typography>
        <Typography sx={{ ...COL_HEAD_SX, textAlign: "right" }}>
          Owner
        </Typography>
        <Typography sx={{ ...COL_HEAD_SX, textAlign: "right" }}>
          Group
        </Typography>
        <Typography sx={{ ...COL_HEAD_SX, textAlign: "right" }}>
          Size
        </Typography>
        <Typography sx={{ ...COL_HEAD_SX, textAlign: "right" }}>
          Modified
        </Typography>
      </Box>

      {/* Rows */}
      <Box
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          outline: "none",
          p: 0.5,
          "&:focus": { outline: "none" },
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={18} sx={{ color: ACCENT }} />
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={recoverFromError}
                sx={{ textTransform: "none", fontWeight: 600, minWidth: 0 }}
              >
                OK
              </Button>
            }
            sx={{ m: 1 }}
          >
            {error}
          </Alert>
        )}

        {!loading &&
          !error &&
          filteredRows.map((row, idx) => {
            const isDir = row.type === "dir";
            const isParent = row._isParent;
            let TileIcon = FolderRoundedIcon;
            if (isParent) TileIcon = SubdirectoryArrowLeftIcon;
            else if (!isDir) TileIcon = getFileGlyph(row.name);
            const isFocused = focusedIdx === idx;

            return (
              <Box
                key={isParent ? ".." : row.name}
                ref={(element) => {
                  rowRefs.current[idx] = element;
                }}
                onClick={() => {
                  setFocusedIdx(idx);
                  activateRow(row);
                }}
                sx={{
                  display: "grid",
                  gridTemplateColumns: GRID_COLS,
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: isComfortable ? 0.85 : 0.5,
                  borderRadius: `${RADIUS.sm}px`,
                  cursor: isDir || onFileOpen ? "pointer" : "default",
                  backgroundColor: isFocused ? ACCENT_TINT : "transparent",
                  "&:hover": {
                    backgroundColor: isFocused ? ACCENT_TINT : HOVER,
                  },
                  "&:hover .rdb-name": {
                    color: isParent ? INK_SECONDARY : ACCENT,
                  },
                  userSelect: "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    minWidth: 0,
                  }}
                >
                  <TileIcon
                    sx={{
                      flexShrink: 0,
                      fontSize: isComfortable ? 20 : 18,
                      color: isDir && !isParent ? ACCENT : ICON,
                    }}
                  />
                  <Typography
                    className="rdb-name"
                    sx={{
                      fontSize: isComfortable ? FONT.md : FONT.sm,
                      color: isParent ? INK_SECONDARY : INK,
                      fontWeight: isDir ? WEIGHT.medium : WEIGHT.regular,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      transition: "color 0.14s ease",
                    }}
                  >
                    {isParent ? ".." : row.name}
                  </Typography>
                </Box>

                <Typography sx={COL_MONO_SX}>
                  {isParent ? "" : row.mode}
                </Typography>
                <Typography sx={COL_MONO_SX}>
                  {isParent ? "" : formatOwner(row.uid, userid, username)}
                </Typography>
                <Typography sx={COL_MONO_SX}>
                  {isParent ? "" : formatOwner(row.gid, userid, username)}
                </Typography>
                <Typography sx={COL_META_SX}>
                  {isParent ? "" : isDir ? "—" : formatSize(row.size)}
                </Typography>
                <Typography sx={COL_META_SX}>
                  {isParent ? "" : formatDate(row.mtime)}
                </Typography>
              </Box>
            );
          })}

        {!loading && !error && truncated && (
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography
              sx={{ fontSize: FONT.xs, color: INK_MUTED, fontStyle: "italic" }}
            >
              Truncated — showing at most {entryCount} item
              {entryCount !== 1 ? "s" : ""}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

RemoteDirectoryBrowser.propTypes = {
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialPath: PropTypes.string,
  onFileOpen: PropTypes.func,
  onSelect: PropTypes.func,
  rowDensity: PropTypes.oneOf(["compact", "comfortable"]),
  rootLabel: PropTypes.string,
  selectable: PropTypes.bool,
  selectLabel: PropTypes.string,
  source: PropTypes.oneOf(["home", "datasets"]),
};
